import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { createQueryParams } from "features/config/helpers";
import {
  useQuery,
  useAppSelector,
  useAppDispatch,
} from "features/config/hooks";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  LuActivity,
  LuCheckCircle,
  LuChevronDown,
  LuChevronRight,
  LuCircle,
  LuClock,
  LuUser,
  LuMoreHorizontal,
  LuCalendarDays,
  LuCalendar,
  LuDownload,
  LuFileImage,
  LuFileSpreadsheet,
  LuFileText,
  LuInfo,
  LuMapPin,
  LuPackage,
  LuTruck,
  LuBoxes,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProductData,
  resetGetProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";
import {
  DisplayData,
  dateSetup,
  useActionAvailability,
} from "./stock-ordering-utils";
import { FaRegEdit } from "react-icons/fa";
import {
  closeStockProcessInfoEditButton,
  selectstockOrderGlobalTrigger,
  toggleStockProcessInfoEditButton,
} from "../slices/stock-order.slice";
import {
  closeModalScroll,
  toggleModalScroll,
} from "../slices/modalScrollSlice.slice";
import { PopupModalV2, PopupV2ButtonConfigs } from "../modals/popup-v2.modal";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  resetstockOrderedEditInfoStatus,
  selectStockOrderedEditInfo,
  stockOrderedEditInfo,
  StockOrderedEditInfoState,
} from "../slices/stock-order-edit-info.slice";
import { StockOrderedEditProductState } from "../slices/stock-order-edit-product.slice";

// Types
interface TimelineStep {
  title: string;
  description: string;
  datetime: string;
  first_name?: string;
  last_name?: string;
  status?: number;
}

interface CustomTimelineProps {
  steps: TimelineStep[];
  className?: string;
  isOrderComplete: boolean;
}

//  Timeline Component - Fixed Overlapping Issues
function CustomTimeline({
  steps,
  className,
  isOrderComplete,
}: CustomTimelineProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  // Memoized timeline calculations
  const timelineData = useMemo(() => {
    const totalSteps = steps.length;
    const completedSteps = isOrderComplete
      ? totalSteps
      : Math.max(0, totalSteps - 1);
    const progressPercentage =
      totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    const currentStatus = isOrderComplete ? "Complete" : "Active";
    const latestStep = steps[steps.length - 1];

    return {
      totalSteps,
      completedSteps,
      progressPercentage,
      currentStatus,
      latestStep,
    };
  }, [steps, isOrderComplete]);

  // Helper functions
  const getStepState = useCallback(
    (step: TimelineStep, index: number) => {
      if (step.status === 10 || isOrderComplete) return "completed";
      if (index === steps.length - 1 && !isOrderComplete) return "current";
      if (index < steps.length - 1) return "completed";
      return "pending";
    },
    [isOrderComplete, steps.length],
  );

  const getStepIcon = useCallback((state: string) => {
    switch (state) {
      case "completed":
        return LuCheckCircle;
      case "current":
        return LuActivity;
      default:
        return LuCircle;
    }
  }, []);

  const formatCompactTime = useCallback((datetime: string) => {
    try {
      const date = new Date(datetime);
      const now = new Date();
      const diffHours = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60),
      );

      if (diffHours < 1) return "Now";
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffHours < 48) return "Yesterday";

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  }, []);

  const {
    totalSteps,
    completedSteps,
    progressPercentage,
    currentStatus,
    latestStep,
  } = timelineData;

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}
    >
      {/* Fixed Compact Header - No Overlapping */}
      <div
        className="px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Top Row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="relative flex-shrink-0">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isOrderComplete ? "bg-green-500" : "bg-blue-500"
                }`}
              >
                {isOrderComplete ? (
                  <LuCheckCircle className="w-3 h-3 text-white" />
                ) : (
                  <LuClock className="w-3 h-3 text-white" />
                )}
              </div>
              {!isOrderComplete && (
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              )}
            </div>

            <div className="flex items-center space-x-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Order Tracking
              </h4>
              <span
                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  isOrderComplete
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {currentStatus}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">
              {completedSteps}/{totalSteps}
            </span>
            <div className="p-0.5">
              {isExpanded ? (
                <LuChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <LuChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row - Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  isOrderComplete ? "bg-green-500" : "bg-blue-500"
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 font-medium shrink-0">
              {Math.round(progressPercentage)}%
            </span>
          </div>

          {/* Latest Update */}
          {latestStep && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Latest:</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900 truncate max-w-32">
                  {latestStep.title}
                </span>
                <span className="text-gray-500 shrink-0">
                  {formatCompactTime(latestStep.datetime)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Timeline Content - Fixed Layout */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Timeline Steps - Fixed Overlapping */}
          <div className="max-h-80 overflow-y-auto">
            <div className="p-4 space-y-3">
              {steps.map((step, index) => {
                const state = getStepState(step, index);
                const IconComponent = getStepIcon(state);
                const userFullName = `${step.first_name || ""} ${
                  step.last_name || ""
                }`.trim();
                const isLast = index === steps.length - 1;

                return (
                  <div key={index} className="relative">
                    {/* Timeline Step Container */}
                    <div className="flex space-x-4">
                      {/* Left Side - Timeline Icon Column */}
                      <div className="flex flex-col items-center space-y-1 shrink-0">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${
                            state === "completed"
                              ? "bg-green-500"
                              : state === "current"
                                ? "bg-blue-500"
                                : "bg-gray-300"
                          }`}
                        >
                          <IconComponent className="w-3 h-3 text-white" />
                        </div>

                        {/* Vertical Line */}
                        {!isLast && <div className="w-px h-12 bg-gray-200" />}
                      </div>

                      {/* Right Side - Content Column */}
                      <div className="flex-1 min-w-0 pb-4">
                        {/* Header Row */}
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex-1 min-w-0 pr-3">
                            <h5 className="text-sm font-medium text-gray-900 leading-tight truncate">
                              {step.title}
                            </h5>
                          </div>
                          <div className="shrink-0 text-right">
                            <span className="text-xs text-gray-500 font-medium">
                              {formatCompactTime(step.datetime)}
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-xs text-gray-600 leading-relaxed mb-2 break-words">
                          {step.description}
                        </p>

                        {/* Bottom Row */}
                        <div className="flex items-center justify-between">
                          {/* Status Badge */}
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              state === "completed"
                                ? "bg-green-100 text-green-700"
                                : state === "current"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {state === "completed"
                              ? "Completed"
                              : state === "current"
                                ? "In Progress"
                                : "Pending"}
                          </span>

                          {/* Time Details */}
                          <div className="flex items-center space-x-1 text-xs text-gray-400">
                            <LuCalendarDays className="w-3 h-3" />
                            <span>
                              {new Date(step.datetime).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}
                            </span>
                          </div>
                        </div>

                        {/* User Info */}
                        {userFullName && (
                          <div className="flex items-center space-x-1 mt-2">
                            <LuUser className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500 truncate">
                              Updated by {userFullName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed Footer - Clean Layout */}
      {!isExpanded && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <span className="text-xs text-gray-600">Steps:</span>
              <div className="flex items-center space-x-1">
                {steps.slice(0, 4).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full ${
                      i < completedSteps
                        ? "bg-green-400"
                        : i === completedSteps
                          ? "bg-blue-400"
                          : "bg-gray-300"
                    }`}
                  />
                ))}
                {steps.length > 4 && (
                  <LuMoreHorizontal className="w-3 h-3 text-gray-400 ml-1" />
                )}
              </div>
            </div>

            <div className="text-xs text-gray-500 shrink-0">
              Click to expand
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Updated main component with better spacing for small screens
export function StockOrderInformation() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { view, orderId } = useParams();

  const [isFilesExpanded, setIsFilesExpanded] = useState(false);
  const [openSaveChanges, setOpenSaveChanges] = useState(false);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<
    string | null
  >(null);

  const managerPerm = useActionAvailability(3);
  const procurementPerm = useActionAvailability(2);
  const globalTriggerState = useAppSelector(selectstockOrderGlobalTrigger);

  const { processEditInfoButton } = globalTriggerState;

  const query = useQuery();

  // Memoize query parameters to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      page_no: query.get("page_no"),
      per_page: query.get("per_page"),
      order_by: query.get("order_by"),
      order: query.get("order"),
      search: query.get("search"),
      status: query.get("status"),
      store: query.get("store"),
      tab: query.get("tab"),
      dateType: query.get("dateType"),
      startDate: query.get("startDate"),
      endDate: query.get("endDate"),
    }),
    [query],
  );

  // Product data selectors
  const getProductDataState = useAppSelector(selectGetProductData);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const stockOrderedEditInfoState = useAppSelector(selectStockOrderedEditInfo);

  // Destructure data
  const { order_information } = getProductDataState.data ?? {};

  //Reset refetch
  useEffect(() => {
    if (
      stockOrderedEditInfoState.status === StockOrderedEditInfoState.success
    ) {
      setRequestedDeliveryDate(null);

      dispatch(resetGetProductData());
      dispatch(getProductData({ orderId: orderId }));

      dispatch(resetstockOrderedEditInfoStatus());
    }
  }, [stockOrderedEditInfoState.status]);

  // Memoize file availability check
  const fileData = useMemo(() => {
    if (!order_information) return { hasFiles: false, files: [] };

    const fileTypes = [
      {
        key: "delivery_receipt",
        title: "Order Sales Invoice",
        icon: LuFileText,
        color: "red",
        type: "ZIP",
        value: order_information.delivery_receipt,
      },
      {
        key: "updated_delivery_receipt",
        title: "Store Sales Invoice",
        icon: LuFileImage,
        color: "blue",
        type: "JPG",
        value: order_information.updated_delivery_receipt,
      },
      {
        key: "discrepancyDate",
        title: "Delivery Adjustment Form",
        icon: LuFileSpreadsheet,
        color: "green",
        type: "PDF",
        value: order_information.discrepancyDate,
        isGeneratable: true,
      },
      {
        key: "updated_supplier_sales_invoice",
        title: "Discrepancy Sales Invoice",
        icon: LuFileText,
        color: "purple",
        type: "PDF",
        value: order_information.updated_supplier_sales_invoice,
      },

      {
        key: "payment_detail_image",
        title: "Uploaded Payment Receipt",
        icon: LuFileText,
        color: "blue",
        type: "JPG",
        value: order_information.payment_detail_image,
      },

      {
        key: "poNumber",
        title: "Uploaded PO Document",
        icon: LuFileText,
        color: "blue",
        type: "PDF",
        value: order_information.poFile,
      },
    ];

    const availableFiles = fileTypes.filter((file) => file.value);
    const hasFiles = availableFiles.length > 0;

    return { hasFiles, files: availableFiles };
  }, [order_information]);

  // Event handlers with useCallback for performance
  const handleDownloadFile = useCallback((type: string) => {
    const link = `${REACT_APP_DOMAIN_URL}api/stock/ordered/download_file_information/${type}`;
    window.open(link, "_blank");
  }, []);

  const handleGenerateForm = useCallback(() => {
    if (!order_information?.id) return;
    const link = `${REACT_APP_DOMAIN_URL}api/stock/delivery-adjustment/${order_information.id}/delivery-adjustment`;
    window.open(link, "_blank");
  }, [order_information?.id]);

  const openAssociatedOrder = useCallback(
    (id: number | null | undefined) => () => {
      const queryString = createQueryParams(queryParams);
      navigate({
        pathname: `/admin/material/${view}/${id}`,
        search: queryString,
      });
    },
    [navigate, view, queryParams],
  );

  const editMode = () => {
    dispatch(toggleStockProcessInfoEditButton());
  };

  const handleCancelEdit = () => {
    dispatch(closeModalScroll());
    setOpenSaveChanges(false);
    dispatch(closeStockProcessInfoEditButton());
  };

  const handleOpenSaveChangesModal = () => {
    dispatch(toggleModalScroll());
    setOpenSaveChanges(true);
  };

  const handleSaveChanges = () => {
    if (orderId) {
      dispatch(
        stockOrderedEditInfo({
          orderId: Number(orderId),
          data: {
            requestedDeliveryDate: requestedDeliveryDate ?? undefined,
          },
        }),
      );
      setOpenSaveChanges(false);
      dispatch(closeModalScroll());
      dispatch(closeStockProcessInfoEditButton());
    }
  };

  const handleUpdateDate = (date: dayjs.Dayjs | null) => {
    setRequestedDeliveryDate(date ? date.format("YYYY-MM-DD") : null);
  };

  /**
   * This checks if if datepicker is null requested delviery is null
   *
   * @note add new
   */
  const changeCheck = processEditInfoButton && requestedDeliveryDate === null;

  // Helper functions
  const renderFileDownloadItem = useCallback(
    (file: any) => {
      const colorClasses = {
        red: { bg: "bg-red-100", icon: "text-red-600" },
        blue: { bg: "bg-blue-100", icon: "text-blue-600" },
        green: { bg: "bg-green-100", icon: "text-green-600" },
        purple: { bg: "bg-purple-100", icon: "text-purple-600" },
      };

      const colors = colorClasses[file.color as keyof typeof colorClasses];
      const IconComponent = file.icon;

      return (
        <div
          key={file.key}
          className="flex flex-col sm:flex-row sm:items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors gap-2"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div
              className={`w-5 h-5 ${colors.bg} rounded flex items-center justify-center flex-shrink-0`}
            >
              <IconComponent className={`w-3 h-3 ${colors.icon}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">
                {file.title}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {file.type} • {file.isGeneratable ? "Generate new" : file.value}
              </p>
            </div>
          </div>
          <button
            onClick={() =>
              file.isGeneratable
                ? handleGenerateForm()
                : handleDownloadFile(file.value)
            }
            className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center space-x-1 min-h-[32px] w-full sm:w-auto"
          >
            <LuDownload className="w-2.5 h-2.5" />
            <span>{file.isGeneratable ? "Generate" : "Get"}</span>
          </button>
        </div>
      );
    },
    [handleDownloadFile, handleGenerateForm],
  );

  if (!order_information) {
    return (
      <div className="w-full bg-white rounded-lg shadow-lg border border-gray-100 p-8 text-center">
        <p className="text-gray-500">Loading order information...</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-2 border-b border-gray-100">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">
            Order Information
          </h2>

          {(managerPerm.permission || procurementPerm.permission) &&
            order_information.status_id === 1 &&
            !processEditInfoButton && (
              <button
                onClick={editMode}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <FaRegEdit />
                <span className="hidden md:block text-sm font-medium">
                  Edit Info
                </span>
              </button>
            )}
        </div>

        <div className="p-2 sm:p-4">
          {/* Restaurant Header - More Compact */}
          <div className="mb-3 p-2.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
            <div className="flex items-start space-x-2.5">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <LuPackage className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col space-y-0.5">
                  {order_information?.companyId === 2 ? (
                    <DisplayData
                      def={order_information?.store_name}
                      alt={order_information?.companyName}
                      defClassName="text-sm sm:text-base font-bold text-gray-900 uppercase"
                    />
                  ) : (
                    <DisplayData
                      def={order_information?.companyName}
                      alt={
                        getAdminSessionState.data?.admin.user_details.company
                          .name
                      }
                      defClassName="text-sm sm:text-base font-bold text-gray-900 uppercase"
                    />
                  )}
                  <DisplayData
                    def={order_information?.ship_to_address}
                    defClassName="text-xs sm:text-sm text-gray-600 capitalize"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {order_information?.isOverride === 1 && (
                    <>
                      <DisplayData
                        def="Override"
                        defClassName="px-1.5 py-0.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded transition-colors"
                      />
                      <span className="text-xs text-gray-600">
                        Min. order overridden
                      </span>
                    </>
                  )}
                  {Boolean(order_information?.managerApproval) && (
                    <div className="flex items-center space-x-1 bg-green-100 px-1.5 py-0.5 rounded-full">
                      <LuCheckCircle className="w-2.5 h-2.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        Manager Approved
                      </span>
                    </div>
                  )}
                  {Boolean(order_information?.procurementApproval) && (
                    <div className="flex items-center space-x-1 bg-green-100 px-1.5 py-0.5 rounded-full">
                      <LuCheckCircle className="w-2.5 h-2.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        Procurement Approved
                      </span>
                    </div>
                  )}

                  {Boolean(order_information?.multimApproval) && (
                    <div className="flex items-center space-x-1 bg-green-100 px-1.5 py-0.5 rounded-full">
                      <LuCheckCircle className="w-2.5 h-2.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        Multim Manager Approved
                      </span>
                    </div>
                  )}

                  {Boolean(order_information?.isPaid) && (
                    <div className="flex items-center space-x-1 bg-green-100 px-1.5 py-0.5 rounded-full">
                      <LuCheckCircle className="w-2.5 h-2.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        Order Paid
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-center bg-white p-1.5 rounded-lg border border-gray-200 flex-shrink-0">
                <p className="text-xs text-gray-500">ID</p>
                <p className="text-xs font-bold text-blue-600">
                  #{order_information?.id}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Main Order Details - Takes 2 columns on large screens */}
            <div className="lg:col-span-2 space-y-3">
              {/* Order Details Grid - More Compact */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {order_information?.poNumber && (
                  <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="w-3 h-3 bg-blue-100 rounded flex items-center justify-center">
                        <LuPackage className="w-3 h-3 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        PO Number
                      </span>
                    </div>
                    <DisplayData
                      def={order_information?.poNumber}
                      defClassName="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded capitalize"
                    />
                  </div>
                )}
                {order_information?.category_name && (
                  <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="w-3 h-3 bg-blue-100 rounded flex items-center justify-center">
                        <LuPackage className="w-3 h-3 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Category
                      </span>
                    </div>
                    <DisplayData
                      def={order_information?.category_name}
                      defClassName="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded capitalize"
                    />
                  </div>
                )}

                {order_information?.fulfillment && (
                  <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="w-3 h-3 bg-blue-100 rounded flex items-center justify-center">
                        <LuBoxes className="w-3 h-3 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Fulfillment Method
                      </span>
                    </div>
                    <DisplayData
                      def={order_information?.fulfillment}
                      defClassName="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded capitalize"
                    />
                  </div>
                )}
                {order_information?.region_name && (
                  <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="w-3 h-3 bg-green-100 rounded flex items-center justify-center">
                        <LuMapPin className="w-3 h-3 text-green-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Region
                      </span>
                    </div>
                    <DisplayData
                      def={order_information?.region_name}
                      defClassName="text-xs font-bold text-gray-900 bg-green-100 px-1.5 py-0.5 rounded"
                    />
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded p-2 shadow-sm col-span-2 sm:col-span-1">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-3 h-3 bg-gray-100 rounded flex items-center justify-center">
                      <LuTruck className="w-3 h-3 text-gray-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-500 uppercase">
                      Transport
                    </span>
                  </div>
                  <DisplayData
                    def={order_information?.transport_route}
                    alt="TBD"
                    defClassName="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded"
                  />
                </div>
              </div>

              {/* Delivery Dates */}
              <div className="bg-white border border-gray-200 rounded p-2.5 shadow-sm">
                <div className="flex items-center space-x-1 mb-2">
                  <div className="w-3 h-3 bg-purple-100 rounded flex items-center justify-center">
                    <LuCalendar className="w-3 h-3 text-purple-600" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900">
                    Delivery Timeline
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-green-50 border border-green-200 rounded">
                    <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <LuCalendar className="w-2 h-2 text-green-600" />
                    </div>
                    <span className="text-xs font-bold text-green-800 block">
                      Requested
                    </span>

                    {processEditInfoButton ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={
                            requestedDeliveryDate
                              ? dayjs(requestedDeliveryDate)
                              : null
                          }
                          onChange={handleUpdateDate}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontSize: "0.75rem", // text-xs
                                  height: "32px", // shrink overall height
                                  paddingRight: "4px",
                                },
                                "& input": {
                                  padding: "4px 6px", // tighter input padding
                                  fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#86efac", // green-300
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#22c55e", // green-500
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#15803d", // green-700
                                  },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    ) : (
                      <p className="text-xs font-bold text-green-900 mt-1">
                        {dateSetup(
                          order_information?.requested_delivery_date ?? "",
                          false,
                        )}
                      </p>
                    )}
                  </div>

                  {order_information?.nextDelivery && (
                    <div className="text-center p-2 bg-green-50 border border-green-200 rounded">
                      <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                        <LuCalendar className="w-3 h-3 text-green-600" />
                      </div>
                      <span className="text-xs font-bold text-green-800 block">
                        Next Delivery
                      </span>
                      <p className="text-xs font-bold text-green-900">
                        {dateSetup(
                          order_information?.nextDelivery ?? "",
                          false,
                        )}
                      </p>
                    </div>
                  )}

                  <div className="text-center p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <LuCheckCircle className="w-3 h-3 text-blue-600" />
                    </div>
                    <span className="text-xs font-bold text-blue-800 block">
                      Committed Delivery
                    </span>
                    <p className="text-xs font-bold text-blue-900">
                      {order_information?.commited_delivery_date
                        ? dateSetup(
                            order_information?.commited_delivery_date ?? "",
                            false,
                          )
                        : "Pending"}
                    </p>
                  </div>

                  <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                    <div className="w-4 h-4 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <LuTruck className="w-3 h-3 text-gray-500" />
                    </div>
                    <span className="text-xs font-bold text-gray-600 block">
                      Actual Delivery
                    </span>
                    <p className="text-xs font-bold text-gray-400">
                      {dateSetup(
                        order_information?.actual_delivery_date ?? "",
                        false,
                      ) || "Pending"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Files Section - Simplified for mobile */}
              {fileData.hasFiles && (
                <div className="bg-white border border-gray-200 rounded shadow-sm overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-indigo-50 to-purple-50 px-2.5 py-2 border-b border-gray-200 cursor-pointer hover:from-indigo-100 hover:to-purple-100 transition-colors"
                    onClick={() => setIsFilesExpanded(!isFilesExpanded)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-indigo-100 rounded flex items-center justify-center">
                          <LuDownload className="w-2 h-2 text-indigo-600" />
                        </div>
                        <h4 className="text-sm font-bold text-gray-900">
                          Downloads ({fileData.files.length})
                        </h4>
                      </div>
                      <LuChevronRight
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          isFilesExpanded ? "rotate-90" : ""
                        }`}
                      />
                    </div>
                  </div>

                  {isFilesExpanded && (
                    <div className="p-2 space-y-1">
                      {fileData.files.map(renderFileDownloadItem)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <CustomTimeline
                steps={order_information?.tracking ?? []}
                isOrderComplete={order_information?.status_id === 10}
                className="mb-3"
              />

              {/* Processing Status - Compact */}
              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2 text-blue-700">
                  <LuInfo className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold">
                      {order_information?.status_id === 10
                        ? "Complete"
                        : "Processing"}
                    </p>
                    <p className="text-xs">
                      {order_information?.status_id === 10
                        ? "Order completed"
                        : "Order in progress"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-t border-gray-200">
          {/* Edit Mode Action Bar */}
          {processEditInfoButton && (
            <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3">
              <div className="flex items-center justify-between">
                {/* Enhanced warning with specific change details */}
                {changeCheck ? (
                  <div className="flex items-center space-x-2 animate-pulse">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>

                    <span className="text-sm text-gray-600">
                      Waiting for changes
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 animate-pulse">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>

                    <span className="text-sm text-gray-600">
                      {(() => {
                        if (requestedDeliveryDate !== null) {
                          return "You have requested delviery date waiting to be saved";
                        }

                        /**
                         * Add new
                         */
                      })()}
                    </span>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleCancelEdit}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={changeCheck}
                    onClick={handleOpenSaveChangesModal}
                    className={`
                                hidden lg:block text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-sm
                                ${
                                  changeCheck
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                                    : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md hover:scale-105 active:scale-95"
                                }
                              `}
                  >
                    Save Changes
                  </button>

                  <button
                    onClick={handleOpenSaveChangesModal}
                    className={`
                                 lg:hidden text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-sm
                                ${
                                  false
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                                    : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md hover:scale-105 active:scale-95"
                                }
                              `}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <PopupModalV2
        open={openSaveChanges}
        onClose={() => {
          setOpenSaveChanges(false);
          dispatch(closeModalScroll());
        }}
        theme="ios"
        buttons={PopupV2ButtonConfigs.saveChanges(
          handleSaveChanges,
          handleCancelEdit,
          () => {
            setOpenSaveChanges(false);
            dispatch(closeModalScroll());
          },
        )}
      />
    </>
  );
}
